import { useEffect, useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@actschurch/ccms-sdk';
import {
  Container,
  Paper,
  Stack,
  Title,
  Text,
  PasswordInput,
  Button,
  Loader,
  Center,
  Alert,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';

type Phase = 'exchanging' | 'linkRequired' | 'redirecting' | 'failed';

/**
 * Planning Centre OAuth callback landing page.
 * Reads `code` + `state` from the URL, calls the SDK callback, then either
 * navigates the user home or prompts for password to confirm a link.
 */
export default function PlanningCentreCallbackPage() {
  const navigate = useNavigate();
  const { planningCentre } = useAuth();

  const [phase, setPhase] = useState<Phase>('exchanging');
  const [error, setError] = useState<string | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [linkEmail, setLinkEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const errParam = params.get('error');

    if (errParam) {
      setError(`Planning Centre returned an error: ${errParam}`);
      setPhase('failed');
      return;
    }

    if (!code || !state) {
      setError('Missing code or state in callback URL');
      setPhase('failed');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const result = await planningCentre.callback(code, state);
        if (cancelled) return;

        if (result.status === 'LoggedIn') {
          setPhase('redirecting');
          navigate('/', { replace: true });
        } else {
          setLinkToken(result.linkToken);
          setLinkEmail(result.email);
          setPhase('linkRequired');
        }
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Sign in failed');
        setPhase('failed');
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onConfirmLink = async (e: FormEvent) => {
    e.preventDefault();
    if (!linkToken) return;
    setConfirming(true);
    setError(null);
    try {
      await planningCentre.confirmLink(linkToken, password);
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err?.message || 'Wrong password?');
    } finally {
      setConfirming(false);
    }
  };

  if (phase === 'exchanging' || phase === 'redirecting') {
    return (
      <Center mih="60vh">
        <Stack align="center">
          <Loader />
          <Text c="dimmed">Signing you in via Planning Centre…</Text>
        </Stack>
      </Center>
    );
  }

  if (phase === 'failed') {
    return (
      <Container size={420} my={60}>
        <Paper withBorder shadow="sm" p="lg" radius="md">
          <Stack gap="md" align="center">
            <Title order={3}>Sign in failed</Title>
            <Text c="dimmed" ta="center">{error}</Text>
            <Button onClick={() => navigate('/auth/login', { replace: true })}>Back to sign in</Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size={420} my={60}>
      <Paper withBorder shadow="sm" p="lg" radius="md">
        <Stack gap="md">
          <Stack gap="xs" align="center">
            <Title order={3} ta="center">Confirm your CCMS password</Title>
            <Text c="dimmed" size="sm" ta="center">
              We found an existing CCMS account for <strong>{linkEmail}</strong>. Enter your password to link
              your Planning Centre account.
            </Text>
          </Stack>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {error}
            </Alert>
          )}

          <form onSubmit={onConfirmLink}>
            <Stack gap="md">
              <PasswordInput
                label="CCMS password"
                placeholder="Your CCMS password"
                required
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
              />
              <Button type="submit" loading={confirming} disabled={!password} fullWidth>
                {confirming ? 'Linking…' : 'Link and sign in'}
              </Button>
              <Button
                type="button"
                variant="subtle"
                fullWidth
                onClick={() => navigate('/auth/login', { replace: true })}
              >
                Cancel
              </Button>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Container>
  );
}
